import { Hidden, Typography } from '@material-ui/core'
import momentJalaali from 'moment-jalaali'
import { useEffect, useRef, useState } from 'react'

import TimePeriodForm from '../../../components/TimePeriodForm'
import ViewSelector from '../../../components/ViewSelector'
import { addDaysToDate, DateObject, getTodayDate } from '../../../helpers/date'
import { getTranslate } from '../../../localization'
import { PaymentTermAssignmentRow } from '../../../types/walletPaymentTerm'
import AssignmentRow from './AssignmentRow'
import DateRow from './DateRow'
import useStyles from './styles/index.styles'

interface PaymentTermAssignmentTableProps {
  assignments: PaymentTermAssignmentRow[]
  setassignments: React.Dispatch<
    React.SetStateAction<PaymentTermAssignmentRow[] | undefined>
  >
  coveringRange: DateObject
  setcoveringRange: React.Dispatch<React.SetStateAction<DateObject>>
}

type RangeType = {
  label: string
  value: 'THREE_MONTH' | 'MONTH' | 'YEAR'
}

export const PaymentTermAssignmentTable = ({
  assignments,
  setassignments,
  coveringRange,
  setcoveringRange
}: PaymentTermAssignmentTableProps) => {
  const classes = useStyles()
  const [selectedRange, setselectedRange] = useState(0)
  const [dateCoveringDatePickerSelected, setdateCoveringDatePickerSelected] =
    useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)

  const coveringDatePickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        coveringDatePickerRef.current &&
        !coveringDatePickerRef?.current?.contains(event.target as Node) &&
        dateCoveringDatePickerSelected
      ) {
        setTimeout(() => {
          setdateCoveringDatePickerSelected(!dateCoveringDatePickerSelected)
        }, 270)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [
    coveringDatePickerRef,
    dateCoveringDatePickerSelected,
    onDoneClickForCloseDatePicker
  ])

  const RANGE_ITEMS: RangeType[] = [
    {
      label: getTranslate('View one Month'),
      value: 'MONTH'
    },
    {
      label: getTranslate('View Three Months'),
      value: 'THREE_MONTH'
    },
    {
      label: getTranslate('View one Year'),
      value: 'YEAR'
    }
  ]

  const [calendarDateRange, setCalendarDateRange] = useState<DateObject>({
    gregorianStartDate: getTodayDate(),
    gregorianEndDate: addDaysToDate(getTodayDate(), 30) // Default to 1 month (30 days)
  })

  useEffect(() => {
    if (coveringRange.gregorianStartDate && coveringRange.gregorianEndDate) {
      // Update calendarDateRange based on selectedRange
      const endDate =
        RANGE_ITEMS[selectedRange].value === 'MONTH'
          ? addDaysToDate(coveringRange.gregorianStartDate, 31)
          : RANGE_ITEMS[selectedRange].value === 'THREE_MONTH'
          ? momentJalaali(coveringRange.gregorianStartDate)
              .startOf('month')
              .add(90, 'days')
              .format('YYYY-MM-DD')
          : momentJalaali(coveringRange.gregorianStartDate)
              .startOf('month')
              .add(365, 'days')
              .format('YYYY-MM-DD') // Default to YEAR if not MONTH or THREE_MONTH

      const startDate =
        RANGE_ITEMS[selectedRange].value === 'MONTH'
          ? coveringRange.gregorianStartDate
          : momentJalaali(coveringRange.gregorianStartDate)
              .startOf('month')
              .format('YYYY-MM-DD') // Default to YEAR if not MONTH or THREE_MONTH

      setCalendarDateRange({
        gregorianStartDate: startDate,
        gregorianEndDate: endDate
      })
    }
  }, [
    selectedRange,
    coveringRange.gregorianStartDate,
    coveringRange.gregorianEndDate
  ])

  return (
    <div className={classes.root}>
      <Hidden smDown>
        <div className={classes.header}>
          <div
            className={classes.datePickerContainer}
            ref={coveringDatePickerRef}>
            <Typography>{getTranslate('Date')}</Typography>
            <TimePeriodForm
              isOpen={dateCoveringDatePickerSelected}
              setTimePickerSelected={setdateCoveringDatePickerSelected}
              dateObject={coveringRange}
              onChange={(data: DateObject) => {
                setcoveringRange(data)
                setassignments((prevAssignments) => {
                  // Ensure prevAssignments is defined
                  if (!prevAssignments) {
                    console.error('prevAssignments is undefined.')
                    return prevAssignments // Avoid errors
                  }

                  return prevAssignments.map((item) => ({
                    ...item,
                    assignments: []
                  }))
                })
              }}
              onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
              setOnDoneClickForCloseDatePicker={
                setOnDoneClickForCloseDatePicker
              }
              left
            />
          </div>
          <ViewSelector
            options={RANGE_ITEMS}
            selectedIndex={selectedRange}
            onSelectionChange={setselectedRange}
          />
        </div>

        {coveringRange &&
          coveringRange.gregorianStartDate &&
          coveringRange.gregorianEndDate && (
            <>
              <DateRow
                selectedDateInDatePicker={calendarDateRange}
                setselectedDateInDatePicker={setCalendarDateRange}
                range={RANGE_ITEMS[selectedRange].value}
              />
              {assignments.map(
                (assignment: PaymentTermAssignmentRow, index) => (
                  <AssignmentRow
                    key={index}
                    setassignments={setassignments}
                    assignment={assignment}
                    range={RANGE_ITEMS[selectedRange].value}
                    calendarDate={calendarDateRange}
                    selectedRangeForAssignments={coveringRange}
                  />
                )
              )}
            </>
          )}
      </Hidden>
    </div>
  )
}
